import { useState } from "react";
import { Link } from "react-router-dom";

import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Textarea } from "components/ui/textarea";

import AddEquipments from "components/generic/AddEquipments";
import AddSkills from "components/crewManagement/AddSkills";

import {
  ArrowLeft,
  CircleDollarSign,
  AlertTriangle,
  CheckCircle2,
  X,
  Check
} from "lucide-react";

import toast from "react-hot-toast";
import { roleOptions, skillOptions } from "data/staticData";
import { signUpStep2 } from "services/authService";

export default function Step2Form({ data, setData, nextStep, prevStep }) {
  const [loading, setLoading] = useState(false);

  /* =========================
     TOAST HELPERS
  ========================== */

  const sectionClasses = "rounded-[12px] border border-[#D8C29A] bg-[#FFFFFF] p-6 space-y-4";


  const toggleRole = (roleValue) => {
    const currentRoles = data.roles || [];
    if (currentRoles.includes(roleValue)) {
      setData({ ...data, roles: currentRoles.filter((r) => r !== roleValue) });
    } else {
      setData({ ...data, roles: [...currentRoles, roleValue] });
    }
  };

  const showErrorToast = (title, message) => {
    toast.custom(
      (t) => (
        <div
          className={`${t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-[#FEF2F2] border border-[#EF4444] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-[#EF4444]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  const showSuccessToast = (title, message) => {
    toast.custom(
      (t) => (
        <div
          className={`${t.visible ? "animate-enter" : "animate-leave"
            } max-w-md w-full bg-[#F0FDF4] border border-[#22C55E] shadow-lg rounded-xl pointer-events-auto flex relative overflow-hidden`}
        >
          <div className="flex-1 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-[#22C55E]" />
                </div>
              </div>
              <div className="ml-3 flex-1 pr-6">
                <p className="text-base font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      ),
      { position: "bottom-right" }
    );
  };

  /* =========================
     SUBMIT (VALIDATION ADDED)
  ========================== */

  const handleSubmit = async () => {
    // 1. Crew Member ID check (Flow check)
    if (!data.crew_member_id) {
      showErrorToast("Invalid Signup Flow", "Please restart the signup process.");
      return;
    }

    // 2. Primary Role Validation
    if (!data.roles) {
      showErrorToast("Validation Error", "Please select a Primary Role.");
      return;
    }

    // 3. Years of Experience Validation
    if (data.yoe === undefined || data.yoe === "" || data.yoe === null) {
      showErrorToast("Validation Error", "Please enter your Years of Experience.");
      return;
    }

    // 4. Hourly Rate Validation
    if (!data.hourlyRate || data.hourlyRate === "") {
      showErrorToast("Validation Error", "Please enter your Hourly Rate.");
      return;
    }

    // 5. Skills Validation (At least one)
    if (!data.skills || data.skills.length === 0) {
      showErrorToast("Validation Error", "Please add at least one skill.");
      return;
    }

    try {
      setLoading(true);

      const rolesArray = (data.roles || []).map((r) =>
        typeof r === "string" ? r : r.value || r.label || ""
      );


      const skills = (data.skills || []).map((s) =>
        typeof s === "string" ? s : s.label
      );

      const equipmentIds = data.equipments || [];

      const payload = {
        crew_member_id: data.crew_member_id,
        primary_role: rolesArray,
        years_of_experience: Number(data.yoe),
        hourly_rate: Number(data.hourlyRate),
        bio: data.bio || "",
        skills: skills,
        equipment_ownership: equipmentIds,
      };

      await signUpStep2(payload);

      showSuccessToast("Saved", "Step 2 completed successfully.");
      nextStep();
    } catch (error) {
      console.error("Step 2 error:", error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Step 2 failed. Please try again.";
      showErrorToast("Step 2 Failed", apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBioChange = (e) => {
    setData({ ...data, bio: e.target.value })
  }

  const cleanBio = () => {
    let value = data.bio

    value = value.replace(/ {3,}/g, "  ")
    value = value.replace(/\n{3,}/g, "\n\n")
    value = value.trim()

    setData({ ...data, bio: value })
  }

  return (
    <div className="flex flex-col gap-10">
      <form className="space-y-4 pl-1" onSubmit={(e) => e.preventDefault()}>
        <div className="flex flex-col gap-4">
          {/* Primary Role */}
          <div className={sectionClasses}>
            <div>
              <h2 className="text-base font-semibold text-black">
                Select Your Role <span className="text-red-600">*</span>
              </h2>
              <p className="text-sm text-black/50">Select at least one role</p>
            </div>
            <div className="flex flex-wrap gap-1 lg:gap-2">
              {roleOptions.map((opt) => {
                const isSelected = data.roles?.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleRole(opt.value)}
                    className={`px-2 py-1 rounded-lg border text-xs lg:text-sm transition-all flex items-center gap-1`}
                    style={{
                      backgroundColor: "#f5f5f5",
                      color: "#333",
                      borderRadius: "8px",
                    }}
                  >
                    {isSelected && <Check className="w-4 h-4" />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>


          {/* Years of Experience and Hourly Rate in Same Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Years of Experience */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">
                Years of Experience <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                placeholder="Enter years"
                value={data.yoe || ""}
                onChange={(e) =>
                  setData({ ...data, yoe: e.target.value })
                }
              />
            </div>

            {/* Hourly Rate */}
            <div className="flex flex-col items-start gap-2 w-full">
              <label className="text-sm font-medium block">
                Hourly Rate <span className="text-red-500">*</span>
              </label>
              <div className="relative w-full">
                <CircleDollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-accent w-5 h-5" />
                <Input
                  type="number"
                  placeholder="Enter your hourly rate"
                  value={data.hourlyRate || ""}
                  className="pl-10"
                  onChange={(e) =>
                    setData({ ...data, hourlyRate: e.target.value })
                  }
                />
              </div>
            </div>
          </div>

          {/* Bio (Optional) */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Bio / About (Optional)
            </label>
            <Textarea
              placeholder="Brief description of expertise and background..."
              value={data.bio || ""}
              maxLength={400}
              onChange={handleBioChange}
              onBlur={cleanBio}
              autoCorrect="off"
              autoCapitalize="none"
            />
          </div>

          {/* Skills */}
          <div className="flex flex-col gap-2 p-4 border-[1.5px] border-[#D8C29A] rounded-lg">
            <label className="text-sm font-bold">
              Skills <span className="text-red-500">*</span>
            </label>
            <AddSkills
              options={skillOptions}
              value={data.skills || []}
              onChange={(v) => setData({ ...data, skills: v })}
            />
          </div>

          {/* Equipment (Optional) */}
          <div className="flex flex-col gap-2 p-4 border-[1.5px] border-[#D8C29A] rounded-lg">
            <label className="text-sm font-bold">Equipment Owned (Optional)</label>
            <AddEquipments
              value={data.equipments || []}
              names={data.equipmentNames || []}
              onChange={(newIds, newNames) =>
                setData({
                  ...data,
                  equipments: newIds,
                  equipmentNames: newNames
                })
              }
            />
          </div>
        </div>

      </form>

      <div className="flex justify-start gap-4">
        <button
          type="button"
          onClick={prevStep}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-black/10 hover:bg-black/5"
        >
          <ArrowLeft className="w-5 h-5 text-black" />
        </button>

        <Button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="flex items-center justify-center rounded-full border border-black/10 hover:bg-black/70 px-4 py-2"
        >
          {loading ? "Saving..." : "Next"}
        </Button>
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-600 mb-10">
        <hr className="flex-grow" />
        <span>Already have an account?</span>
        <Link to="/loginCrew" className="text-black">
          Log in
        </Link>
        <hr className="flex-grow" />
      </div>
    </div>
  );
}
